import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Flag, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { FlagReviewFields } from '../components/caseload-flags/FlagReviewFields';
import { FlagHistory } from '../components/caseload-flags/FlagHistory';
import { SourceNotesCard, ComparisonCard, FollowUpCard, ManagementReviewCard } from '../components/caseload-flags/TicketSections';
import { flagApi, notifyFlagUpdated, fmtWhen, FLAG_STATUS_STYLES } from '../components/caseload-flags/flagUtils';

const CM_ACTION = ['Flagged', 'In Progress', 'Returned for Correction'];

const Info = ({ label, value, testid }) => (
  <div><div className="text-xs text-muted-foreground">{label}</div><div className="text-sm font-medium" data-testid={testid}>{value || '-'}</div></div>
);

const ReviewInfo = ({ flag, record, task }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <Info label="Participant" value={flag.participant_name} testid="ticket-participant" />
    <Info label="State ID" value={record?.state_id_number} />
    <Info label="Week of" value={flag.week_of_date} />
    <Info label="Program" value={record?.program_name || flag.program_type} />
    <Info label="Case Manager" value={flag.case_manager_name} testid="ticket-case-manager" />
    {flag.flag_for === 'success_coach' && <Info label="Flag For" value={`Success coach: ${flag.assignee_name || '-'}`} testid="ticket-flag-for" />}
    <Info label="Enroll Date" value={record?.enrollment_start_date} />
    <Info label="Course Status" value={record?.course_status} />
    <Info label="Correction Task" value={task ? `${task.status === 'completed' ? 'Completed' : 'Open'} · due ${task.due_date}` : null} testid="ticket-task" />
  </div>
);

const CommentBox = ({ busy, onAdd }) => {
  const [note, setNote] = useState('');
  return (
    <div className="space-y-2">
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a comment to the ticket" data-testid="flag-note-input" />
      <div className="flex justify-end"><Button variant="outline" disabled={busy || !note.trim()} onClick={() => onAdd(note).then((ok) => ok && setNote(''))} data-testid="flag-add-comment-btn"><MessageSquare className="h-4 w-4 mr-2" />Add Comment</Button></div>
    </div>
  );
};

const useTicket = (flagId) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setData(await flagApi.detail(flagId)); } catch (err) { setError(err.response?.data?.detail || 'Could not load this ticket'); }
  }, [flagId]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); notifyFlagUpdated(); return true; } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); return false; } finally { setBusy(false); }
  };
  return { data, error, busy, load, run };
};

export default function CaseloadFlagTicketPage() {
  const { flagId } = useParams();
  const { data, error, busy, load, run } = useTicket(flagId);
  const [savingField, setSavingField] = useState(null);

  const saveField = async (field, value) => {
    setSavingField(field);
    try { await flagApi.draftField(data.flag.id, field, value); toast.success('Change kept on the ticket'); await load(); notifyFlagUpdated(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not save'); } finally { setSavingField(null); }
  };

  if (error) return (
    <div className="text-center py-16 text-muted-foreground space-y-3" data-testid="ticket-error">
      <p>{error}</p>
      <Link to="/dashboard" className="text-sm inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-4 w-4" />Dashboard</Link>
    </div>
  );
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const { flag } = data;
  const cmCanAct = data.is_case_manager && CM_ACTION.includes(flag.status);
  const canEdit = flag.status !== 'Resolved' && (data.can_manage || cmCanAct);
  return (
    <div className="space-y-6" data-testid="caseload-flag-ticket-page">
      <div>
        <Link to={data.can_manage ? '/reports/caseload-review-tickets' : '/dashboard'} className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="ticket-back-link">
          <ArrowLeft className="h-4 w-4" />{data.can_manage ? 'Caseload Review Tickets' : 'Dashboard'}
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2 mt-1 flex-wrap"><Flag className="h-6 w-6 text-red-500" />Caseload Review Ticket
          <Badge variant="outline" className={FLAG_STATUS_STYLES[flag.status]} data-testid="ticket-status">{flag.status}</Badge></h1>
        <p className="text-sm text-muted-foreground">Ticket {flag.id} · Flagged by {flag.flagged_by_name} on {fmtWhen(flag.created_at)}</p>
      </div>
      <Card className="card-shadow"><CardHeader><CardTitle className="text-lg">Participant & Review Details</CardTitle></CardHeader>
        <CardContent><ReviewInfo flag={flag} record={data.record} task={data.task} /></CardContent></Card>
      <Card className="card-shadow border-red-200 dark:border-red-800" data-testid="ticket-comments">
        <CardHeader><CardTitle className="text-lg">Management Comments & Required Corrections</CardTitle></CardHeader>
        <CardContent className="space-y-3"><p className="text-sm whitespace-pre-wrap">{flag.comments}</p>
          {flag.return_comment && <div className="rounded-md bg-orange-50 dark:bg-orange-900/10 p-2 text-sm" data-testid="ticket-return-comment"><span className="font-medium">Returned by {flag.returned_by_name}:</span> {flag.return_comment}</div>}
        </CardContent></Card>
      {data.can_manage && flag.status === 'Pending Management Review' && (
        <ManagementReviewCard busy={busy} onApprove={(c) => run(() => flagApi.approve(flag.id, c), 'Approved and resolved')}
          onReturn={(c) => run(() => flagApi.returnFlag(flag.id, c), 'Returned to the Case Manager')} />
      )}
      <Card className="card-shadow">
        <CardHeader><CardTitle className="text-lg">Corrections</CardTitle><CardDescription>These start from the values saved on the Caseload Review. Your changes are kept on this ticket and saved to the review when you submit corrections.{data.draft_fields?.length ? ` ${data.draft_fields.length} change${data.draft_fields.length > 1 ? 's' : ''} waiting to be submitted.` : ''}</CardDescription></CardHeader>
        <CardContent>
          {data.record ? <FlagReviewFields record={data.record} options={data.dropdown_options} onSave={saveField} savingField={savingField} disabled={!canEdit} />
            : <p className="text-sm text-muted-foreground">The review record is no longer available.</p>}
        </CardContent>
      </Card>
      <SourceNotesCard notes={data.source_notes || []} canEdit={canEdit} onSave={(id, text) => run(() => flagApi.draftNote(flag.id, id, text), 'Case note change kept on the ticket')} />
      <ComparisonCard rows={data.comparison || []} />
      <FollowUpCard flag={flag} canAct={cmCanAct} busy={busy} onGenerate={() => run(() => flagApi.followUp(flag.id), 'Follow-up summary generated')}
        onSubmit={(n) => run(() => flagApi.submit(flag.id, n), 'Corrections submitted for management approval')} />
      <Card className="card-shadow"><CardHeader><CardTitle className="text-lg">History</CardTitle></CardHeader>
        <CardContent className="space-y-4">{flag.status !== 'Resolved' && <CommentBox busy={busy} onAdd={(n) => run(() => flagApi.comment(flag.id, n), 'Comment added')} />}
          <FlagHistory history={flag.history} /></CardContent></Card>
    </div>
  );
}
